import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { useCookieConsent } from './CookieConsentContext';

const contentUseCase = new ContentUseCase();

export const CookieBanner: React.FC = () => {
  const { language } = useLanguage();
  const content = contentUseCase.getCookieBanner(language);
  const { showBanner, setCookieConsent } = useCookieConsent();
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [languageEnabled, setLanguageEnabled] = useState(false);

  const handleAcceptAll = () => {
    setCookieConsent('accepted', { preferences: true, analytics: true });
  };

  const handleSaveSettings = () => {
    // Check if user selected anything
    if (analyticsEnabled || languageEnabled) {
      // User selected at least one option - accept with selected preferences
      setCookieConsent('accepted', {
        preferences: languageEnabled,
        analytics: analyticsEnabled
      });
    } else {
      // User declined everything - store declined state
      setCookieConsent('declined');
    }

    setShowSettings(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
      }}
    >
      <Box sx={{ maxWidth: 'lg', mx: 'auto', p: 2 }}>
        <Paper
          elevation={3}
          sx={{
            p: 3,
            borderRadius: 2,
            backgroundColor: 'background.paper',
          }}
        >
          <Typography variant="h6" gutterBottom>
            {content.title}
          </Typography>

          <Typography variant="body2" paragraph>
            {content.description}
          </Typography>

          <Typography variant="body2" paragraph>
            {content.tracking}{' '}
            <Link href="/cookies" color="primary" underline="hover">
              {content.cookiePolicy}
            </Link>
            {language === 'de' ? ' und ' : ' and '}
            <Link href="/privacy" color="primary" underline="hover">
              {content.privacyPolicy}
            </Link>.
          </Typography>

          {!showSettings ? (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => setShowSettings(true)}
                sx={{ minWidth: 120 }}
              >
                {content.settings}
              </Button>
              <Button
                variant="contained"
                onClick={handleAcceptAll}
                sx={{ minWidth: 120 }}
              >
                {content.acceptAll}
              </Button>
            </Box>
          ) : (
            <Box>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {language === 'de' ? 'Cookie-Einstellungen' : 'Cookie Settings'}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {language === 'de'
                        ? 'Wählen Sie aus, welche Cookies Sie zulassen möchten:'
                        : 'Choose which cookies you want to allow:'
                      }
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={languageEnabled}
                            onChange={(e) => setLanguageEnabled(e.target.checked)}
                          />
                        }
                        label={language === 'de' ? 'Spracheinstellung' : 'Language Preference'}
                      />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                        {language === 'de'
                          ? 'Diese Einstellung speichert Ihre bevorzugte Sprache.'
                          : 'This setting stores your preferred language.'
                        }
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={analyticsEnabled}
                            onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                          />
                        }
                        label={language === 'de' ? 'Analyse-Cookies (Google Analytics)' : 'Analytics Cookies (Google Analytics)'}
                      />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                        {language === 'de'
                          ? 'Diese Cookies helfen uns zu verstehen, wie Besucher unsere Website nutzen.'
                          : 'These cookies help us understand how visitors use our website.'
                        }
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={() => setShowSettings(false)}
                    >
                      {language === 'de' ? 'Abbrechen' : 'Cancel'}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSaveSettings}
                    >
                      {language === 'de' ? 'Speichern' : 'Save'}
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};
